export const SYSTEM = `You are a senior healthcare operations and patient experience consultant with 20+ years auditing medical practice websites. You write strategic, specific, patient-centered audit reports. Every finding is tied to patient acquisition, experience, or operational outcomes. Never use generic statements. Be specific to the practice type, specialty, market, and patient population. Do not give legal or medical advice. Flag compliance items as general observations only.`;

const SCORE_TABLE = `| Category | Max | Score | Rationale |
|---|---|---|---|
| First Impression & Brand Clarity | 10 | X/10 | [specific] |
| Patient Experience & Usability | 15 | X/15 | [specific] |
| Conversion Optimization | 15 | X/15 | [specific] |
| Trust & Credibility | 10 | X/10 | [specific] |
| Patient Information Architecture | 10 | X/10 | [specific] |
| Accessibility & Inclusivity | 10 | X/10 | [specific] |
| SEO Foundations | 10 | X/10 | [specific] |
| Content Quality | 10 | X/10 | [specific] |
| Operational Support | 5 | X/5 | [specific] |
| Compliance & Risk Awareness | 5 | X/5 | [specific] |`;

export function buildPrompt(f, concise) {
  const date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const talk = f.talking === 'yes' ? `
## Consultant Talking Points
**Opening Frame:** [Establish credibility with practice leadership]
**Key Messages:** [3-5 bullets tied to patient volume, trust, revenue]
**Anticipated Objections:** [2-3 pushbacks and responses]
**Recommended Next Steps:** [Concrete follow-on to propose]
` : '';

  const formatting = `
CRITICAL FORMATTING RULES:
1. Write all content as complete flowing sentences within single paragraphs.
2. Do NOT break sentences across multiple lines.
3. Each bullet must be ONE complete self-contained sentence. Do NOT add a follow-on line starting with — or a continuation after the bullet.
4. Each labeled field (**Observations:**, **Why It Matters:**, **Recommendations:**) must be a single continuous paragraph.
5. Quick Wins and Strategic Recommendations must each be a single complete sentence on one line.
`;

  if (concise) return `Conduct a concise healthcare website audit. Use web search to visit ${f.url} first.
${formatting}
Practice: ${f.name} | URL: ${f.url} | Specialty: ${f.spec} | Patients: ${f.pts} | Market: ${f.geo}
Notes: ${f.notes || 'None'} | Date: ${date}

# ${f.name} - Website Audit Summary
*${f.spec} | ${f.geo} | ${date}*

## Executive Summary
[2-3 paragraphs with specific observations from the actual site]

## Score Overview
**Overall Score: [X]/100**
${SCORE_TABLE}

## Top Strengths
[4-6 specific bullets, each a complete self-contained sentence]

## Critical Gaps
[4-6 specific bullets with patient/revenue impact built in, each a complete sentence]

## Quick Wins
1. 2. 3. 4. 5.

## Top 3 Priorities
**#1 - [Title]:** [2-3 sentences]
**#2 - [Title]:** [2-3 sentences]
**#3 - [Title]:** [2-3 sentences]
${talk}`;

  return `Conduct a comprehensive healthcare website audit. Use web search to visit ${f.url} and search for "${f.name} ${f.geo}" first.
${formatting}
Practice: ${f.name} | URL: ${f.url} | Specialty: ${f.spec} | Patients: ${f.pts} | Market: ${f.geo}
Notes: ${f.notes || 'None'} | Date: ${date}

Write the COMPLETE audit. Do not truncate any section.

# ${f.name} - Healthcare Website Audit Report
*${f.spec} | ${f.geo} | ${date}*

## Executive Summary
[3-4 paragraphs with specific observations from the actual website]

## Overall Score
**Overall Score: [X]/100**
${SCORE_TABLE}
[1 paragraph scoring rationale]

## Strengths
[6-8 specific bullets, each a complete self-contained sentence. NO follow-on lines.]

## Priority Gaps
[6-8 specific bullets with patient/revenue impact built into each bullet. NO follow-on lines.]

## Detailed Findings by Category

### 1. First Impression & Brand Clarity - [X]/10
**Observations:** [Complete paragraph]
**Why It Matters:** [Complete paragraph]
**Recommendations:** [Complete paragraph with 2-4 items separated by semicolons]

### 2. Patient Experience & Usability - [X]/15
**Observations:** [Complete paragraph]
**Why It Matters:** [Complete paragraph]
**Recommendations:** [Complete paragraph]

### 3. Conversion Optimization - [X]/15
**Observations:** [Complete paragraph]
**Why It Matters:** [Complete paragraph]
**Recommendations:** [Complete paragraph]

### 4. Trust & Credibility - [X]/10
**Observations:** [Complete paragraph]
**Why It Matters:** [Complete paragraph]
**Recommendations:** [Complete paragraph]

### 5. Patient Information Architecture - [X]/10
**Observations:** [Complete paragraph]
**Why It Matters:** [Complete paragraph]
**Recommendations:** [Complete paragraph]

### 6. Accessibility & Inclusivity - [X]/10
**Observations:** [Complete paragraph]
**Why It Matters:** [Complete paragraph]
**Recommendations:** [Complete paragraph]

### 7. SEO Foundations - [X]/10
**Observations:** [Complete paragraph]
**Why It Matters:** [Complete paragraph]
**Recommendations:** [Complete paragraph]

### 8. Content Quality - [X]/10
**Observations:** [Complete paragraph]
**Why It Matters:** [Complete paragraph]
**Recommendations:** [Complete paragraph]

### 9. Operational Support - [X]/5
**Observations:** [Complete paragraph]
**Why It Matters:** [Complete paragraph]
**Recommendations:** [Complete paragraph]

### 10. Compliance & Risk Awareness - [X]/5
*General observations only - not legal advice.*
**Observations:** [Complete paragraph]
**Recommendations:** [Complete paragraph]

## Quick Wins
[8 numbered improvements, each a single complete sentence on one line]

## Strategic Recommendations
[5 numbered recommendations, each a complete paragraph on one line]

## Patient Journey Perspective

### Initial Exposure
[Complete paragraph]

### Inquiry & Scheduling
[Complete paragraph]

### Pre-Visit Preparation
[Complete paragraph]

### Visit Readiness
[Complete paragraph]

### Post-Visit Continuity
[Complete paragraph]

## Top 3 Priorities

**#1 - [Title]**
[Complete paragraph]

**#2 - [Title]**
[Complete paragraph]

**#3 - [Title]**
[Complete paragraph]
${talk}`;
}
